import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { pickLocale } from "@/lib/helpers";
import POICard from "@/components/POICard";
import * as Icons from "lucide-react";
import { Loader2 } from "lucide-react";

export default function CategoryList() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [category, setCategory] = useState(null);
  const [pois, setPois] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [cat, list] = await Promise.all([
          api.get(`/public/categories/${slug}`),
          api.get(`/public/pois?category_slug=${slug}`),
        ]);
        setCategory(cat.data);
        setPois(list.data || []);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  if (loading) return <div className="flex items-center justify-center py-20 text-ink-300"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (notFound || !category) return (
    <div className="px-5 pt-16 text-center animate-fade-up">
      <div className="font-editorial italic text-3xl text-ink-900">…</div>
      <p className="text-sm text-ink-300 mt-2" data-testid="category-not-found">{t("poi.no_items")}</p>
    </div>
  );

  const Icon = Icons[category.icon] || Icons.MapPin;
  const featured = pois.filter((p) => p.featured);
  const rest = pois.filter((p) => !p.featured);

  return (
    <div className="px-5 pt-4">
      <div className="text-center pt-4 animate-fade-up">
        <div className="mx-auto w-12 h-12 rounded-2xl bg-bronze-50 flex items-center justify-center">
          <Icon strokeWidth={1.4} className="w-5 h-5 text-bronze-500" />
        </div>
        <h1 data-testid="category-title" className="font-editorial italic text-[38px] text-ink-900 mt-3 leading-tight">
          {pickLocale(category.name, i18n.language)}
        </h1>
        {pois.length > 0 && (
          <div
            data-testid="category-count"
            className="text-[10px] font-semibold tracking-overline text-bronze-500 mt-1"
          >
            {t("poi.count", { count: pois.length })}
          </div>
        )}
        <p
          data-testid="category-tagline"
          className="font-editorial italic text-[15px] text-ink-500 mt-2"
        >
          {t("tagline.category")}
        </p>
        <div className="flex justify-center my-3"><span className="ornament" /></div>
      </div>

      {pois.length === 0 && (
        <p className="text-center text-sm text-ink-300 py-16" data-testid="category-empty">
          {t("poi.no_items")}
        </p>
      )}

      {featured.length > 0 && (
        <section className="mt-4 flex flex-col gap-3 animate-fade-up" style={{ animationDelay: "60ms" }}>
          {featured.map((p) => <POICard key={p.id} poi={p} />)}
        </section>
      )}

      {rest.length > 0 && (
        <section className="mt-3 flex flex-col gap-3 animate-fade-up" style={{ animationDelay: "120ms" }}>
          {rest.map((p) => <POICard key={p.id} poi={p} />)}
        </section>
      )}

      {/* Discoverability hint: guide guests to the map for the full picture. */}
      {pois.length > 0 && (
        <Link
          to="/mappa"
          data-testid="category-map-hint"
          className="mt-6 mb-4 flex items-center justify-center gap-2 text-[11px] font-medium text-ink-300 hover:text-bronze-500 transition"
        >
          <Icons.Map strokeWidth={1.4} className="w-3.5 h-3.5" />
          <span>{t("poi.more_on_map")}</span>
          <Icons.ArrowRight strokeWidth={1.4} className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
}
